import { ImageBackground, ScrollView, View } from "react-native";
import { Div, Icon, Label, Touchable, useWindowSize } from "@grassrootly/rn-core";
import { images } from "../../../layout/assets";
import moment from "moment";
import NextGameActions from "./NextGameActions";
import { NextGameProps } from "./NextGame.types";
import { Image } from "react-native";
import { isFinished } from "../lib";

const TeamName = ({ name, isMobile, classNames }: { name: string; isMobile: boolean; classNames?: string[] }) => {
  return (
    <Label
      classNames={classNames || ["ml-{5}", "mr-{5}"]}
      numberOfLines={2}
      ellipsizeMode="tail"
      type={!isMobile ? "l1Bold" : "l2Bold"}
    >
      {name}
    </Label>
  );
};

const TeamLogo = ({ logo, isMobile }: { logo: string; isMobile: boolean }) => {
  return (
    <Div
      classNames={[
        "p-{2}",
        "bgSecondaryMain",
        "borderPrimary",
        "bw-{1}",
        "h-{30}",
        "w-{30}",
        "br-{30}",
        "alignItemsCenter",
        "justifyContentCenter",
      ]}
    >
      <Label numberOfLines={1} type={!isMobile ? "l1Regular" : "l2Regular"}>
        {logo}
      </Label>
    </Div>
  );
};

export default function NextGame({ game, navigation, setGameAttendancy, updateAttendancyInProgress }: NextGameProps) {
  const { isMobile } = useWindowSize();

  const onMatchesPress = (gameId: number) => {
    navigation?.navigate("game", { id: gameId });
  };

  // Determine if the match is in the future or past
  const isMatchInFuture = moment(game.game_datetime).isAfter(moment());
  const sectionTitle = isMatchInFuture ? "Your Next Match" : "Your featured match";

  return (
    <View style={{ flex: 1, width: "100%" }}>
      <Touchable onPress={() => onMatchesPress(game.game_id)}>
        <ImageBackground
          resizeMode="cover"
          imageStyle={{ borderRadius: 8 }}
          source={images.bgNextMatchWidget}
          style={{ width: "100%", minHeight: !isMobile ? 320 : 160 }}
        >
          {(game.season_summary || (game.game_type === "TRAINING" && game.group_name && !game.season_summary)) && (
            <Div classNames={["alignSelfCenter", "flexRow", "alignItemsCenter", "justifyContentCenter", "gap8", "flexWrap", "zIndex2", `${!isMobile ? "mt-{8}" : ""}`]}>
              <Div classNames={["flexRow", "alignItemsCenter", "gap8"]}>
                {game.season_summary ? (
                  <>
                    {game.organization_logo && (
                      <Image
                        source={{ uri: game.organization_logo }}
                        style={{ height: !isMobile ? 20 : 16, width: !isMobile ? 20 : 16, borderRadius: 4 }}
                        resizeMode="cover"
                      />
                    )}
                    <Div classNames={["bgSecondaryMain", "rounded"]}>
                      <Label classNames={[!isMobile ? "ph16" : "ph8", "pv2"]} type={!isMobile ? "l2Regular" : "l4Regular"}>
                        {game.season_summary}
                      </Label>
                    </Div>
                  </>
                ) : (
                  <>
                    {game.group_logo && (
                      <Image
                        source={{ uri: game.group_logo }}
                        style={{ height: !isMobile ? 20 : 16, width: !isMobile ? 20 : 16, borderRadius: 4 }}
                        resizeMode="cover"
                      />
                    )}
                    <Div classNames={["bgSecondaryMain", "rounded"]}>
                      <Label classNames={[!isMobile ? "ph16" : "ph8", "pv2"]} type={!isMobile ? "l2Regular" : "l4Regular"}>
                        {game.group_name}
                      </Label>
                    </Div>
                  </>
                )}
              </Div>
            </Div>
          )}
          <Div
            classNames={[
              `${!isMobile ? "gap2" : ""}`,
              "alignSelfCenter",
              "bgTransparentInfo",
              "rounded",
              "p16",
              "absolute",
              "w-{100%}",
              "h-{100%}",
              "zIndex1",
              "alignItemsCenter",
              "alignContentCenter",
              "justifyContentCenter",
            ]}
          >
            {/* Date and time - moved to top */}
            <Div classNames={["flexRow", "alignItemsCenter", "justifyContentCenter", "gap8", `${!isMobile ? "mb-{32}" : "mb-{12}"}`]}>
              <Label numberOfLines={1} type={!isMobile ? "l2Bold" : "l3Bold"}>
                {moment(game.game_datetime).format("DD.MM.YY HH:mm")}
              </Label>
              {game.game_type === "TRAINING" && (
                <Div classNames={["bgSecondaryLight", "rounded"]}>
                  <Label classNames={["ph8", "pv2"]} type={!isMobile ? "l3Regular" : "l4Regular"}>
                    Training
                  </Label>
                </Div>
              )}
            </Div>

            {/* Teams */}
            <Div classNames={["flexRow", "alignItemsCenter", "justifyContentCenter", "gap16"]}>
              {/* Host team */}
              <Div classNames={["flexColumn", "alignItemsCenter", "flex1", "justifyContentCenter"]}>
                <Image
                  style={{ height: !isMobile ? 60 : 45, width: !isMobile ? 60 : 45, borderRadius: 60 }}
                  resizeMode="cover"
                  source={game.host_team_logo ? { uri: game.host_team_logo } : images.whiteTeamLogo}
                />
                <TeamName
                  name={game.game_host_team_name || "Beli"}
                  isMobile={isMobile}
                  classNames={["mt-{8}", "textCenter"]}
                />
              </Div>
              
              {/* Score section */}
              <Div classNames={["flexColumn", "alignItemsCenter", "justifyContentCenter"]}>
                {isFinished(game) ? (
                  <Div classNames={["flexRow", "alignItemsCenter", "gap8"]}>
                    <Label type="l1Bold" classNames={[!isMobile ? "fontSize32" : "fontSize24"]}>
                      {game.game_host_score || 0}
                    </Label>
                    <Label type="l1Bold" classNames={[!isMobile ? "fontSize32" : "fontSize24"]}>-</Label>
                    <Label type="l1Bold" classNames={[!isMobile ? "fontSize32" : "fontSize24"]}>
                      {game.game_visitor_score || 0}
                    </Label>
                  </Div>
                ) : (
                  <Label type="l1Bold" classNames={[!isMobile ? "fontSize32" : "fontSize24"]}>-</Label>
                )}
              </Div>
              
              {/* Visitor team */}
              <Div classNames={["flexColumn", "alignItemsCenter", "flex1", "justifyContentCenter"]}>
                <Image
                  style={{ height: !isMobile ? 60 : 45, width: !isMobile ? 60 : 45, borderRadius: 60 }}
                  resizeMode="cover"
                  source={game.visitor_team_logo ? { uri: game.visitor_team_logo } : images.blackTeamLogo}
                />
                <TeamName
                  name={game.game_visitor_team_name || "Markeri"}
                  isMobile={isMobile}
                  classNames={["mt-{8}", "textCenter"]}
                />
              </Div>
            </Div>
          </Div>
        </ImageBackground>
      </Touchable>

      <NextGameActions
        game={game}
        navigation={navigation}
        setGameAttendancy={setGameAttendancy}
        updateAttendancyInProgress={updateAttendancyInProgress}
      ></NextGameActions>
    </View>
  );
}
